import type { AwsOuNode } from '@/api/awsOrgProvisioning'

/** Tenant minimo necessario para derivar escopo por OU. */
export interface OuScopeTenant {
  id: number
  name: string
  environment: string
  ou_id?: string | null
}

/** Conjunto de ou_ids descendentes (inclusivo) das OUs informadas, a partir
 * da arvore local de aws_ou_nodes (walk por parent_ou_id). Espelha
 * `_descendant_ou_ids` do backend (rbac.py). */
export function descendantOuIds(nodes: AwsOuNode[], rootOuIds: string[]): Set<string> {
  const childrenByParent = new Map<string, string[]>()
  for (const n of nodes) {
    if (!n.parent_ou_id) continue
    const arr = childrenByParent.get(n.parent_ou_id) ?? []
    arr.push(n.ou_id)
    childrenByParent.set(n.parent_ou_id, arr)
  }
  const result = new Set<string>(rootOuIds)
  const stack = [...rootOuIds]
  while (stack.length) {
    const cur = stack.pop()!
    for (const child of childrenByParent.get(cur) ?? []) {
      if (!result.has(child)) {
        result.add(child)
        stack.push(child)
      }
    }
  }
  return result
}

/** Tenants derivados do escopo de OU: contas cujo ou_id cai no subtree das
 * OUs selecionadas. Espelha `tenants_under_ous` do backend. */
export function tenantsUnderOus<T extends OuScopeTenant>(
  allTenants: T[],
  nodes: AwsOuNode[],
  ouIds: string[],
): T[] {
  if (ouIds.length === 0) return []
  const descendants = descendantOuIds(nodes, ouIds)
  return allTenants.filter((t) => t.ou_id != null && descendants.has(t.ou_id))
}
